import { useState, useEffect, useContext } from 'react';

import { Button, Grid, Box, TextField, MenuItem } from '@material-ui/core';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import LinearProgress from '@material-ui/core/LinearProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { debounce } from 'lodash';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

// context
import Store from '../../store';
import Actions from '../../store/actions';

import themev5 from '../../themev5';
import Styles from './styles/CategorizationTable.styles';
import styles from '../Datagrid/Datagrid.styles';
import DatagridFooter from '../Datagrid/DatagridFooter';
import DatagridNoRowsOverlay from '../Datagrid/DatagridNoRowsOverlay';

// api
import { getCderLettersList } from '../../api/Cder';
import LetterPageEditDialog from './LetterPageEditDialog';
import LetterPmrDialog from './LetterPmrDialog';
import LabelCurationLegend from './legends/LabelCurationLegend';

const qcStatusMap = (key: string) => {
  const map: any = {
    to_qc: 'To QC',
    qced: 'QCed',
    escalate: 'Escalate',
    'missing details': 'Missing Details'
  };
  return map[key];
};

const LetterCuration = () => {
  const { dispatch } = useContext<any>(Store);
  const classes = Styles();
  const [pageSize, setPageSize] = useState(10);
  const [letterData, setLetterData] = useState<Array<any>>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState();
  const [loading, setloading] = useState(true);
  const [letterEditVisible, setLetterEditVisible] = useState(false);
  const [pmrEditVisible, setPmrEditVisible] = useState(false);
  const [docsId, setDocsId] = useState<Number>();
  const [searchText, setSearchText] = useState<string>('');
  const [status, setStatus] = useState<string>('all documents');
  const [curationTask, setCurationTask] = useState<string>('0');
  const [submissionType, setSubmissionType] = useState<string>('');
  const [createAt, setCreatedAt] = useState<string>('');
  const rowsPerPageOptions = [10, 20, 50, 100];
  const [reload, setReload] = useState(false);

  const debouncedChangeHandler = () => {
    setReload(prevState => !prevState);
  };
  const handleChange = debounce(debouncedChangeHandler, 300);

  useEffect(() => {
    const fetchCderLetterData = async () => {
      try {
        const result = await getCderLettersList(
          pageNumber,
          pageSize,
          status,
          searchText.trim().toLowerCase(),
          curationTask,
          createAt,
          submissionType
        );

        if (result === undefined) {
          throw new Error('Result is undefined');
        }
        if (result.data && result.data.body.results) {
          setloading(false);
          const resultData = result.data.body.results;
          setTotalRowCount(result.data.body.total_rows);
          setLetterData(resultData?.map((item: any, index: any) => ({ ...item, id: index })));
        } else {
          setloading(false);
          throw Error('No data');
        }
      } catch (e: any) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: e.message }
        });
      }
    };
    fetchCderLetterData();
  }, [pageNumber, status, pageSize, reload, curationTask, createAt, submissionType]);

  const openEdit = (data: any) => {
    setDocsId(data.document_id);
  };
  const closeEdit = () => {
    setLetterEditVisible(false);
    setPmrEditVisible(false);
    setReload(prevState => !prevState);
  };

  const letterCurationNext = () => {
    closeEdit();
    setPmrEditVisible(true);
  };

  const getQCButtonClass = (row: any, key: string): any => {
    const qcStatus: string = row.qc_statuses[key].toLowerCase();
    const qcStatusColorMap: any = {
      qced: classes.Button,
      to_qc: classes.BlueButton,
      default: classes.YellowButton
    };
    return qcStatusColorMap[qcStatus] || qcStatusColorMap.default;
  };

  const columns = [
    {
      field: 'application_number',
      headerName: 'Application Number',
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'pdf_title',
      headerName: 'PDF Title',
      width: 500,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return (
          <Grid item xs={8} lg={8}>
            {params.row.s3_path.split('/').slice(-1)}
          </Grid>
        );
      }
    },
    {
      field: 'overall_qc_status',
      headerName: 'Overall QC Status',
      width: 170,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return (
          <Grid item xs={8} lg={8}>
            {qcStatusMap(params.row.overall_qc_status as string)}
          </Grid>
        );
      }
    },
    {
      field: 'submission_no',
      headerName: 'Submission Number',
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'submission_type',
      headerName: 'Submission Type',
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'created_at',
      headerName: 'Created date',
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'Actions',
      width: 350,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return (
          <Box
            style={{
              margin: '3px',
              display: 'flex',
              width: '100%',
              justifyContent: 'space-around'
            }}>
            <Button
              variant='contained'
              className={getQCButtonClass(params.row, 'letter_curation')}
              onClick={() => {
                openEdit(params.row);
                setLetterEditVisible(true);
              }}>
              <Typography variant='body1'>Letter Curation</Typography>
            </Button>
            <Button
              variant='contained'
              className={getQCButtonClass(params.row, 'pmr/c')}
              onClick={() => {
                openEdit(params.row);
                setPmrEditVisible(true);
              }}>
              <Typography variant='body1'>PMR Curation</Typography>
            </Button>
          </Box>
        );
      }
    }
  ];
  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Grid container>
        <Grid item xl={10} lg={10}>
          <Box display='flex' ml={2} mt={10} alignItems='center'>
            <TextField
              variant='outlined'
              type='search'
              id='search_application'
              label='Search Application Number'
              value={searchText}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}
              onChange={e => {
                setSearchText(e.target.value);
                setPageNumber(0);
                handleChange();
              }}
            />
            <TextField
              variant='outlined'
              id='status'
              label='QC Status'
              select
              value={status}
              onChange={e => setStatus(e.target.value)}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}>
              <MenuItem value='all documents'>All Documents</MenuItem>
              <MenuItem value='qced'>Qced</MenuItem>
              <MenuItem value='to_qc'>To QC</MenuItem>
              <MenuItem value='escalate'>Escalate</MenuItem>
              <MenuItem value='missing details'>Missing Details</MenuItem>
            </TextField>
            <TextField
              variant='outlined'
              id='curation'
              label='Curation Task'
              select
              value={curationTask}
              onChange={e => setCurationTask(e.target.value)}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}>
              <MenuItem value='0'>All</MenuItem>
              <MenuItem value='1'>Letter Curation</MenuItem>
              <MenuItem value='2'>PMR Curation</MenuItem>
            </TextField>
            <TextField
              variant='outlined'
              id='submissiontype'
              label='Submission Type'
              select
              value={submissionType}
              onChange={e => setSubmissionType(e.target.value)}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}>
              <MenuItem value='All'>All</MenuItem>
              <MenuItem value='ORIG'>ORIG</MenuItem>
              <MenuItem value='SUPPL'>SUPPL</MenuItem>
            </TextField>
            <Box sx={{ width: '300px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label='Create At Date'
                  inputFormat='yyyy-MM-dd'
                  openTo='year'
                  views={['year', 'month', 'day']}
                  value={createAt || null}
                  onChange={newValue => {
                    if (newValue !== null && typeof newValue !== 'undefined') {
                      // eslint-disable-next-line no-restricted-globals
                      if (!isNaN(new Date(newValue).getTime())) {
                        const date = format(new Date(newValue), 'yyyy-MM-dd');
                        setCreatedAt(date);
                      }
                    }
                  }}
                  InputProps={{
                    style: { fontSize: '1.5vh', minWidth: '250px', width: '300px' }
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      fullWidth
                      variant='outlined'
                      InputLabelProps={{
                        style: { fontSize: '1.5vh', fontWeight: 'bold', Width: '300px' }
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Grid>
        <Grid item lg={2} xl={2}>
          <Box mt={14}>
            <LabelCurationLegend />
          </Box>
        </Grid>
      </Grid>
      <Box
        height='80vh'
        p={2}
        mt={1}
        display='flex'
        alignItems='center'
        justifyContent='center'
        className={classes.root}>
        <DataGrid
          sx={styles.datagrid}
          rowSpacingType='margin'
          sortingOrder={['desc', 'asc']}
          checkboxSelection={false}
          density='standard'
          rows={letterData}
          columns={columns as any}
          rowCount={totalRowCount}
          pageSize={pageSize}
          getRowId={row => row.id}
          onPageSizeChange={newPageSize => {
            setPageSize(newPageSize);
            setPageNumber(0);
          }}
          getRowHeight={() => 'auto'}
          onPageChange={setPageNumber}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          loading={loading}
          getRowClassName={() => `table-rows`}
          page={pageNumber}
          paginationMode='server'
          components={{
            LoadingOverlay: LinearProgress,
            Footer: DatagridFooter,
            NoRowsOverlay: DatagridNoRowsOverlay
          }}
        />
        <Dialog
          open={letterEditVisible || pmrEditVisible}
          onClose={closeEdit}
          sx={{ backdropFilter: 'blur(5px)' }}
          maxWidth={pmrEditVisible ? 'xl' : 'lg'}
          fullWidth>
          {letterEditVisible ? (
            <LetterPageEditDialog
              closeEdit={closeEdit}
              docsId={docsId}
              handleNext={letterCurationNext}
            />
          ) : null}
          {pmrEditVisible ? <LetterPmrDialog closeEdit={closeEdit} docsId={docsId} /> : null}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default LetterCuration;
